import type { FocusEvent, FormEvent, KeyboardEvent } from 'react';

import { z } from 'zod';

import { DateHelpers } from '../../../shared/lib/date';
import { sumFormFormatter, sumSubmitFormatter } from '../../../shared/lib/formatters';

import type { TDealDetails, TUpdateDealDetailsFieldName } from '@crm/entities/deal';
import type { TFunction } from 'i18next';

export type FieldValue = string | number | null;
export type EditableElement = HTMLInputElement | HTMLTextAreaElement;
export type InfoFieldName = TUpdateDealDetailsFieldName | 'deal_details';
export type FieldErrors = Partial<Record<TUpdateDealDetailsFieldName, string>>;

export type InfoItem = {
    label: string;
    rawValue?: FieldValue;
    value: FieldValue;
    fieldName: InfoFieldName;
    placeholder?: string;
};

export type UpdateFieldHandler = (
    fieldName: TUpdateDealDetailsFieldName,
    initialValue: FieldValue,
    event: FocusEvent<EditableElement>
) => void;

export type UpdateFieldKeyDownHandler = (
    fieldName: TUpdateDealDetailsFieldName,
    initialValue: FieldValue,
    event: KeyboardEvent<EditableElement>
) => void;

export type FieldFocusHandler = (
    fieldName: TUpdateDealDetailsFieldName,
    event: FocusEvent<HTMLInputElement>
) => void;

export type FieldInputHandler = (
    fieldName: TUpdateDealDetailsFieldName,
    event: FormEvent<EditableElement>
) => void;

export type FieldEditHandler = (fieldName: TUpdateDealDetailsFieldName) => void;

export type DateFieldCommitHandler = (
    fieldName: TUpdateDealDetailsFieldName,
    initialValue: FieldValue,
    nextValue: string
) => void;

export const EMAIL_MAX_LENGTH = 255;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isValidEmail = (value: string) => value === '' || EMAIL_PATTERN.test(value);

export const createEditableFieldValidationSchemas = (t: TFunction) => {
    const customerNameRequiredMessage = t(
        'crm.dealDetailsContent.informationBlock.validation.customerNameRequired'
    );

    return new Map<TUpdateDealDetailsFieldName, z.ZodType<string>>([
        [
            'customer_name',
            z
                .string(customerNameRequiredMessage)
                .trim()
                .min(1, customerNameRequiredMessage)
                .max(
                    255,
                    t('crm.dealDetailsContent.informationBlock.validation.customerNameMaxLength', {
                        max: 255,
                    })
                ),
        ],
        [
            'email',
            z
                .string()
                .trim()
                .max(
                    EMAIL_MAX_LENGTH,
                    t('crm.dealDetailsContent.informationBlock.validation.emailMaxLength', {
                        max: EMAIL_MAX_LENGTH,
                    })
                )
                .refine(
                    isValidEmail,
                    t('crm.dealDetailsContent.informationBlock.validation.invalidEmail')
                ),
        ],
        [
            'received_at',
            z
                .string()
                .trim()
                .refine(
                    (value) => value === '' || DateHelpers.parseDisplayDate(value) !== undefined,
                    t('crm.common.validation.invalidDate')
                ),
        ],
    ]);
};

export const isUpdateDealDetailsFieldName = (
    fieldName: InfoFieldName
): fieldName is TUpdateDealDetailsFieldName => fieldName !== 'deal_details';

export const isSumField = (fieldName: TUpdateDealDetailsFieldName) =>
    fieldName === 'area' || fieldName === 'approximate_budget';

export const isDateField = (fieldName: InfoFieldName) =>
    fieldName === 'received_at' || fieldName === 'expected_closing_at';

export const formatSumInputValue = (value: FieldValue) =>
    sumFormFormatter(String(value ?? ''));

const formatIntegerWithSpaces = (value: string) => value.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

export const formatSumFocusValue = (value: FieldValue) => {
    const formattedValue = formatSumInputValue(value);

    if (!formattedValue.includes(',')) {
        return formattedValue;
    }

    const [integerPart, fractionPart = ''] = formattedValue.split(',');
    const trimmedFractionPart = fractionPart.replace(/0+$/, '');

    return trimmedFractionPart ? `${integerPart},${trimmedFractionPart}` : integerPart;
};

export const formatSumDisplayValue = (value: FieldValue) => {
    const formattedValue = formatSumInputValue(value);

    if (!formattedValue) {
        return '';
    }

    const [integerPart, fractionPart = ''] = formattedValue.split(',');
    const formattedIntegerPart = formatIntegerWithSpaces(integerPart);
    const trimmedFractionPart = fractionPart.replace(/0+$/, '');

    if (!trimmedFractionPart) {
        return formattedIntegerPart;
    }

    return `${formattedIntegerPart},${trimmedFractionPart.slice(0, 2)}`;
};

export const formatDateValue = (value: string | null | undefined) => {
    if (!value) {
        return '';
    }

    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? value : DateHelpers.formatDisplayDate(date);
};

export const getSubmitValue = (fieldName: TUpdateDealDetailsFieldName, value: string) => {
    if (fieldName === 'phone') {
        return value.trim();
    }

    if (fieldName === 'email') {
        const normalizedEmail = value.trim();

        return isValidEmail(normalizedEmail) ? normalizedEmail : undefined;
    }

    if (isSumField(fieldName)) {
        return sumSubmitFormatter(value);
    }

    if (!isDateField(fieldName)) {
        return value;
    }

    const normalizedDate = value.trim();

    if (!normalizedDate) {
        return null;
    }

    const date = DateHelpers.parseDisplayDate(normalizedDate);

    return date ? DateHelpers.formatApiDate(date) : undefined;
};

export const getCurrentSubmitValue = (
    dealData: TDealDetails['dealData'] | undefined,
    fieldName: TUpdateDealDetailsFieldName,
    initialValue: FieldValue
) => {
    if (fieldName === 'received_at') {
        return String(dealData?.receivedAt ?? '');
    }

    if (fieldName === 'expected_closing_at') {
        return String(dealData?.expectedClosingAt ?? '');
    }

    if (fieldName === 'phone') {
        return String(dealData?.phone ?? '').trim();
    }

    if (isSumField(fieldName)) {
        return sumSubmitFormatter(initialValue);
    }

    return String(initialValue ?? '');
};
